<template>
    <div class="profile-edition__skills_item" :class="{'profile-edition__skills_item--disabled': !!disabled}" :data-testid="`${dataTestid}-item`">
        <VJoyTooltip v-bind="{}" :data-testid="`${dataTestid}-tooltip`">
            <VJoyTag
                :id="`${dataTestid}-tag`"
                :variant="variant"
                class="profile-edition__skills_item__tag"
                :size="size"
                :data-testid="`${dataTestid}-tag`"
            >
                <VJoyIcon
                    v-if="isEnabledModeFavorite"
                    class="profile-edition__skills_item__tag__icon"
                    :class="{'profile-edition__skills_item__tag__icon--disabled': isDisabledFavoriteCTA}"
                    :name="favoriteIcon"
                    :data-testid="`${dataTestid}-icon-favorite`"
                    @click="handleFavoriteToggle"
                />
                <div class="profile-edition__skills_item__tag__content" :data-testid="`${dataTestid}-content`">
                    <VJoyLink
                        v-if="hasLink"
                        :href="href!"
                        :rel="isSearchLink ? 'nofollow' : undefined"
                        class="profile-edition__skills_item__tag__link"
                        :data-testid="`${dataTestid}-link`"
                    >
                        <div class="profile-edition__skills_item__tag__link__content"><slot /></div>
                    </VJoyLink>
                    <slot v-else />
                </div>
                <VJoyIcon
                    v-if="isAddModeEnabled"
                    class="profile-edition__skills_item__tag__icon"
                    :class="{'profile-edition__skills_item__tag__icon--disabled': isAddCtaDisabled}"
                    name="add"
                    :data-testid="`${dataTestid}-icon-add`"
                    @click="handleAdd"
                />
                <VJoyIcon
                    v-if="isEnabledModeRemove"
                    class="profile-edition__skills_item__tag__icon"
                    name="cross"
                    :data-testid="`${dataTestid}-icon-remove`"
                    @click="handleRemove"
                />
            </VJoyTag>
            <template v-if="isEnabledTooltip" #tooltip-content>
                <div class="profile-edition__skills_item__tooltip" :data-testid="`${dataTestid}-tooltip-content`"><slot /></div>
            </template>
        </VJoyTooltip>
    </div>
</template>
<script setup lang="ts">
    import {computed} from 'vue';
    import {type TJoyTagSizes, type TJoyTagVariants, VJoyIcon, VJoyLink, VJoyTag, VJoyTooltip} from '@maltjoy/core-vue';

    /**
     * enabled: visible and clickabke
     * disabled: visible but not clickable
     * hidden: not visible nor clickable
     */
    export type ProfileSkillSetItemCTAMode = 'enabled' | 'disabled' | 'hidden';

    const props = withDefaults(
        defineProps<{
            dataTestid?: string;
            disabled?: boolean;
            favorite?: boolean;
            href?: string;
            variant?: TJoyTagVariants;
            isEnabledModeRemove?: boolean;
            isEnabledTooltip?: boolean;
            modeAdd?: ProfileSkillSetItemCTAMode;
            modeFavorite?: ProfileSkillSetItemCTAMode;
            size?: TJoyTagSizes;
        }>(),
        {
            dataTestid: 'profile-skill-set-item',
            disabled: false,
            favorite: false,
            href: undefined,
            variant: 'secondary',
            isEnabledModeRemove: false,
            isEnabledTooltip: true,
            modeAdd: 'hidden',
            modeFavorite: 'hidden',
            size: 'medium',
        },
    );

    const emit = defineEmits<{
        add: [];
        'favorite:add': [];
        'favorite:remove': [];
        remove: [];
    }>();

    // Add
    const isAddCtaDisabled = computed(() => props.modeAdd === 'disabled');
    const isAddModeEnabled = computed(() => props.modeAdd === 'enabled' || props.modeAdd === 'disabled');
    // Favorite
    const isDisabledFavoriteCTA = computed(() => props.modeFavorite === 'disabled');
    const isEnabledModeFavorite = computed(() => props.modeFavorite === 'enabled' || props.modeFavorite === 'disabled');
    const isFavoriteModeRemove = computed(() => props.favorite === true);
    const favoriteIcon = computed(() => (isFavoriteModeRemove.value ? 'star-fill' : 'star'));

    const hasLink = computed(() => !props.disabled && props.href && props.href.length > 0);
    const isSearchLink = computed(() => props.href?.startsWith('/s?q='));
    const isEnabledTooltip = computed(() => !!props.isEnabledTooltip);

    function handleAdd() {
        if (!props.disabled && !isAddCtaDisabled.value) {
            emit('add');
        }
    }
    function handleFavoriteToggle() {
        if (!props.disabled && !isDisabledFavoriteCTA.value) {
            if (isFavoriteModeRemove.value) {
                emit('favorite:remove');
            } else {
                emit('favorite:add');
            }
        }
    }

    function handleRemove() {
        if (!props.disabled) {
            emit('remove');
        }
    }
</script>
<style lang="scss">
    .profile-edition__skills_item {
        max-width: 100%;

        &__tag {
            &__content,
            &__link__content {
                max-width: 218px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            &__icon {
                cursor: pointer;

                &--disabled {
                    cursor: not-allowed;
                    * {
                        color: var(--joy-color-neutral-40);
                    }
                }
            }
        }

        &__tooltip {
            word-break: normal;
            overflow-wrap: anywhere;
            text-align: left;
        }

        &--disabled {
            .joy-tag {
                color: var(--joy-color-neutral-40);
            }

            .profile-edition__skills_item__tag__icon {
                cursor: not-allowed;
            }
        }
    }
</style>
